'use client'

import { Check, Star, Zap } from 'lucide-react'
import Link from 'next/link'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

const marketingPlans = [
  {
    name: 'Standard',
    price: '$49',
    billing: 'one-time',
    guests: 'Up to 75 guests',
    storage: '75GB storage',
    highlights: [
      'QR code and share-link access',
      'Photos and videos in one gallery',
      'No recurring fees',
    ],
  },
  {
    name: 'Host Annual',
    price: '$79',
    billing: 'per year',
    guests: 'Unlimited events',
    storage: '100GB storage',
    highlights: [
      'Everything in Standard',
      'Unlimited event collections',
      'Annual savings vs. buying 2× Standard',
    ],
    featured: true,
    badge: 'Most Popular',
    checkoutPath: '/api/checkout/host-annual',
  },
  {
    name: 'Premium Event',
    price: '$99',
    billing: 'one-time',
    guests: 'Up to 200 guests',
    storage: '200GB storage',
    highlights: [
      'Everything in Standard',
      'More guest and storage headroom',
      'Best fit for larger single events',
    ],
  },
]

interface PricingCardsProps {
  id?: string
  showHeader?: boolean
}

export function PricingCards({ id = 'pricing', showHeader = true }: PricingCardsProps) {
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false)

  async function handleHostAnnualCheckout() {
    setIsLoadingCheckout(true)
    try {
      const res = await fetch('/api/checkout/host-annual', { method: 'POST' })
      const data = await res.json() as { url?: string; error?: string }
      if (data.url) {
        window.location.href = data.url
      } else {
        alert(data.error ?? 'Something went wrong. Please try again.')
      }
    } catch {
      alert('Something went wrong. Please try again.')
    } finally {
      setIsLoadingCheckout(false)
    }
  }

  return (
    <section id={id} className="w-full py-12 md:py-24 lg:py-32 bg-gradient-to-b from-background to-primary/5">
      <div className="container px-4 md:px-6">
        {showHeader ? (
          <div className="flex flex-col items-center justify-center space-y-4 text-center mb-10 md:mb-16">
            <div className="space-y-2">
              <div className="inline-block rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                Flexible Pricing
              </div>
              <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight gradient-heading">
                Pick the Plan That Fits
              </h2>
              <p className="max-w-[900px] text-muted-foreground md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
                One-time purchases for single events, or subscribe annually for unlimited hosting.
              </p>
            </div>
          </div>
        ) : null}

        <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
          {marketingPlans.map((plan) => (
            <Card
              key={plan.name}
              className={`relative overflow-hidden ${
                plan.featured ? 'border-primary shadow-xl md:scale-[1.03]' : ''
              }`}
            >
              {plan.badge ? (
                <div className="absolute right-0 top-0 flex items-center gap-1 rounded-bl-lg bg-primary px-3 py-1 text-xs text-primary-foreground">
                  <Star className="h-3 w-3" />
                  {plan.badge}
                </div>
              ) : null}

              <CardContent className="p-6">
                <h3 className="text-xl font-bold">{plan.name}</h3>

                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-sm text-muted-foreground">/{plan.billing}</span>
                </div>

                {plan.name === 'Host Annual' ? (
                  <p className="mt-1 text-xs text-primary font-medium">
                    Save vs. buying Standard twice
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {plan.billing === 'one-time' ? 'One-time purchase' : ''}
                  </p>
                )}

                <ul className="mt-6 space-y-3">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                    <span>{plan.guests}</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                    <span>{plan.storage}</span>
                  </li>
                  {plan.highlights.map((highlight) => (
                    <li key={highlight} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6">
                  {plan.checkoutPath ? (
                    <Button
                      className="w-full"
                      disabled={isLoadingCheckout}
                      onClick={() => void handleHostAnnualCheckout()}
                    >
                      {isLoadingCheckout ? (
                        <span className="flex items-center gap-2">
                          <Zap className="h-4 w-4 animate-pulse" />
                          Redirecting…
                        </span>
                      ) : (
                        'Start Host Annual'
                      )}
                    </Button>
                  ) : (
                    <Link href="/auth/register">
                      <Button variant={plan.featured ? 'default' : 'outline'} className="w-full">
                        Get {plan.name}
                      </Button>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

      </div>
    </section>
  )
}
